import React, { FC, ReactNode, useCallback, useState } from 'react';

import computeScrollIntoView from 'compute-scroll-into-view';

import Button from '@adsk/alloy-react-button';
import {
  DropdownMenu,
  useDropdown,
  UseDropdownProps,
  PLACEMENTS,
} from '@adsk/alloy-react-dropdown';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { StyleProp } from '@adsk/alloy-react-theme';

type Item = {
  key: number;
  label: ReactNode;
};

type YearMonthDropdownProps = Pick<
  UseDropdownProps<Item>,
  'items' | 'selectedItem' | 'onSelectedItemChange'
> & {
  children: ReactNode;
  items: Item[];
  style?: StyleProp;
  disabled?: boolean;
  'data-testid'?: string;
};

const YearMonthDropdown: FC<YearMonthDropdownProps> = ({
  style,
  children,
  disabled,
  'data-testid': dataTestId,
  ...useDropdownProps
}) => {
  const [isInitialScroll, setIsInitialScroll] = useState(true);

  /**
   * This is the default downshift behaviour, modified on initial scroll
   * https://github.com/downshift-js/downshift/blob/bcc793a194a7c30f84b4514b8541a6b7ee3d991f/src/utils.js#L25-L39
   */
  const centerOnInitialScroll = useCallback(
    (node: HTMLElement, menuNode: HTMLElement) => {
      const actions = computeScrollIntoView(node, {
        boundary: menuNode,
        block: isInitialScroll ? 'center' : 'nearest',
        scrollMode: 'if-needed',
      });
      actions.forEach(({ el, top, left }) => {
        el.scrollTop = top;
        el.scrollLeft = left;
      });

      setIsInitialScroll(false);
    },
    [isInitialScroll],
  );

  const theme = useTheme();
  const t = theme.tokens?.component?.datePicker;
  const dropdownIconColor = t?.yearMonthDropdown?.icon?.color as
    | string
    | undefined;
  const dropdownIconFill = t?.yearMonthDropdown?.icon?.fill as
    | string
    | undefined;

  const { controlProps, menuProps } = useDropdown<Item, HTMLDivElement>({
    placement: PLACEMENTS.BOTTOM_START,
    scrollIntoView: centerOnInitialScroll,
    ...useDropdownProps,
  });

  const { ref, isOpen, getToggleButtonProps } = controlProps;

  return (
    <div ref={ref}>
      <Button
        data-testid={dataTestId}
        style={[
          {
            paddingLeft: t?.yearMonthDropdown?._?.paddingLeft,
            paddingRight: t?.yearMonthDropdown?._?.paddingRight,
            border: 'none',
            width: 'auto',
            color: t?.yearMonthDropdown?._?.color,
            fontFamily: t?.yearMonthDropdown?._?.fontFamily,
            fontSize: t?.yearMonthDropdown?._?.fontSize,
            fontWeight: t?.yearMonthDropdown?._?.fontWeight,
            lineHeight: t?.yearMonthDropdown?._?.lineHeight,
            '&:disabled': {
              color: t?.yearMonthDropdown?.disabled?.true?.color,
            },
            '& > div': {
              justifyContent: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
            },
            '& svg, & svg path': {
              color: dropdownIconColor,
              fill: dropdownIconFill,
            },
          },
          style,
        ]}
        variant={Button.VARIANTS.TERTIARY}
        {...getToggleButtonProps()}
        selected={isOpen}
        disabled={disabled}
      >
        {children}
        <ChevronDownIcon
          style={{
            marginLeft: t?.yearMonthDropdown?.icon?.marginLeft,
            width: t?.yearMonthDropdown?.icon?.width,
            height: t?.yearMonthDropdown?.icon?.height,
            color: dropdownIconColor,
            fill: dropdownIconFill,
          }}
        />
      </Button>
      <DropdownMenu
        style={{
          width: 130,
          maxHeight: 272,
          overflowY: 'auto',
        }}
        renderFooter={() => null} // Cannot clear this dropdown, so no footer needed
        {...menuProps}
      />
    </div>
  );
};

YearMonthDropdown.displayName = 'YearMonthDropdown';

export default YearMonthDropdown;
